import { Text } from "@/components/podkit/typography/Text";
import { type PlainWorkflow, useListWorkflowExecutions } from "@/queries/automation-queries";
import { getMostRecentExecution, workflowName, isWorkflowDeleting } from "@/utils/automations";
import { type FC, useMemo, useState } from "react";
import { WorkflowExecutionStatusIcon } from "@/components/automations/AutomationExecutionStatusIcon";
import { WorkflowExecutionStatusText } from "@/components/automations/AutomationExecutionStatusText";
import { AutomationUserDisplay } from "./AutomationUserDisplay";
import { AutomationPropertiesHeader } from "@/routes/automations/AutomationPropertiesHeader";
import { cn, type PropsWithClassName } from "@/components/podkit/lib/cn";
import { WorkflowActionsDropdown } from "@/components/automations/AutomationActionsDropdown.tsx";
import { IconSpinner } from "@/assets/icons/geist/IconSpinner";

type WorkflowCardProps = {
    workflow: PlainWorkflow;
    readonly?: boolean;
} & PropsWithClassName;

export const AutomationCard: FC<WorkflowCardProps> = ({ workflow, className, readonly = false }) => {
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);

    const { data: executions } = useListWorkflowExecutions({
        enabled: true,
        workflowId: workflow.id,
    });

    const mostRecentExecution = useMemo(() => getMostRecentExecution(executions), [executions]);

    const hasDescription = !!workflow.metadata?.description;
    const creator = workflow.metadata?.creator;
    const isDeleting = isWorkflowDeleting(workflow);
    const showActionsMenu = !readonly;

    return (
        <div
            className={cn(
                "flex flex-col gap-3 rounded-xl border border-border-base p-4",
                { "shadow-sm": isDropdownOpen },
                { "hover:shadow-sm": !readonly },
                className,
            )}
        >
            <div className={cn(isDeleting && "pointer-events-none")}>
                <div className="flex items-center justify-between gap-2">
                    <Text className="text-base font-medium text-content-primary">{workflowName(workflow)}</Text>
                    {showActionsMenu && (
                        <div className={cn(isDeleting && "pointer-events-auto")}>
                            <WorkflowActionsDropdown
                                workflow={workflow}
                                showRunAction={true}
                                buttonSize={"sm"}
                                onOpenChange={setIsDropdownOpen}
                            />
                        </div>
                    )}
                </div>
                {hasDescription ? (
                    <Text className="text-base text-content-strong">{workflow.metadata?.description}</Text>
                ) : (
                    <div className="mt-2">
                        <AutomationPropertiesHeader workflow={workflow} />
                    </div>
                )}
            </div>
            <div className={cn("flex items-center gap-1", isDeleting && "pointer-events-none")}>
                <AutomationUserDisplay subject={creator} />
                <span className="text-content-muted">·</span>
                {isDeleting ? (
                    <>
                        <IconSpinner size="sm" className="shrink-0 animate-spin text-content-muted" />
                        <Text className="text-sm text-content-muted">Deleting</Text>
                    </>
                ) : (
                    <>
                        <WorkflowExecutionStatusIcon execution={mostRecentExecution} />
                        <WorkflowExecutionStatusText execution={mostRecentExecution} />
                    </>
                )}
            </div>
        </div>
    );
};
